/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from "react";

import DateFnsUtils from "@date-io/date-fns";
import { add, startOfDay } from "date-fns";

import {
  Avatar,
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  Button as MuiButton,
  TextField as MuiTextfield,
  Switch,
  Tooltip,
  Typography,
} from "@material-ui/core";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import PublicIcon from "@material-ui/icons/Public";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { blue } from "@material-ui/core/colors";
import { Calendar, MuiPickersUtilsProvider } from "@material-ui/pickers";

import { Button, useNotify } from "react-admin";

import { AppType, EventType, TimeZoneOptions } from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import {
  fetchAllEventTypes,
  fetchYardTimeslotsForDay,
  formatYardScheduleDate,
  getEventTypeCap,
  getOccupiedIntervals,
  getYardCalendarDateParts,
  getYardNow,
  isSameYardScheduleDay,
  isSlotBlocked,
  timeStringToMinutes,
  tryGetDateTimeFromTimeslot,
  validateAppointment,
} from "../../../utils/dateUtils";
import { CancelIcon, CheckIcon, ClearIcon } from "../iconography";
import "./Scheduler.css";
import { fetchEventTypes } from "../../../managerApp/resources/eventTypes/EventTypeUtils";
import {
  canOverrideScheduling,
  finalizeAppointmentSubmit,
  getDismissedSchedulerState,
  generateDayTimeslots,
  initializeSchedulingState,
  resolveSchedulerOpenDay,
  shouldDisableDate,
} from "./SchedulerUtils";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: 9999,
  },
  timeslots: {
    flex: 1,
    overflowX: "clip",
    overflowY: "auto",
    marginLeft: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  timeslot: {
    textTransform: "none",
    marginTop: "2px",
  },
  // Full slots the manager override leaves clickable — warning-coloured so
  // they read as "this overbooks the yard", not as an ordinary free slot.
  timeslotAtCapacity: {
    borderColor: theme.palette.warning.main,
    color: theme.palette.warning.main,
  },
  timeslotAtCapacitySelected: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
  dialogActions: {
    marginRight: theme.spacing(2),
  },
  dialogContent: {
    // https://stackoverflow.com/questions/47698037/how-can-i-set-a-height-to-a-dialog-in-material-ui
    height: "100vh",
    maxWidth: "500px",
    padding: theme.spacing(2),
  },
  dialogCloseIcon: {
    position: "absolute",
    right: theme.spacing(0.5),
    top: theme.spacing(0.5),
  },
  selectedDateAvatar: {
    // backgroundColor: theme.palette.type === "dark" ? blue[400] : blue[50],
    margin: theme.spacing(0.15),
    backgroundColor: blue[600],
    width: theme.spacing(4.5),
    height: theme.spacing(4.5),
    color: "white",
    fontSize: 14,
  },
  dateAvatar: {
    margin: theme.spacing(0.15),
    width: theme.spacing(4.5),
    height: theme.spacing(4.5),
    color: "white",
    fontSize: 14,
  },
  spacer: {
    flexGrow: 1, // Takes up all available space
  },
  // Inline mode sits inside a form column rather than over the page, so the
  // veil and the two panels are sized against that column instead of the
  // viewport the dialog owns.
  inlineRoot: {
    position: "relative",
  },
  inlineBackdrop: {
    // Absolute against inlineRoot, so it veils the scheduler's own column
    // rather than the page; z-index only has to beat its unlayered siblings.
    position: "absolute",
    zIndex: 1,
  },
  inlineCalendar: {
    flex: "0 0 auto",
  },
  inlineTimeslots: {
    flex: "1 1 130px",
    minWidth: 120,
    maxHeight: 340,
    overflowY: "auto",
    marginLeft: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
}));

const getYardTimeZoneName = () => {
  const tz = window.appUser?.reloadYard?.timeZone;
  const match = TimeZoneOptions.find((opt) => opt.id === tz);
  return match ? match.name : tz;
};

const init = (selectedTimeslot, eventType, override) =>
  initializeSchedulingState(selectedTimeslot, eventType, override);

const getCapitalizedResourceName = (eventType) => {
  if (!eventType.resourceName) return "";
  return (
    eventType.resourceName[0].toUpperCase() +
    eventType.resourceName.substring(1)
  );
};

const getTitle = (evtType, appType) => {
  // Showw asset name only to yard managerr / supervisor
  if (appType === AppType.YARD && evtType?.resourceName) {
    const name = getCapitalizedResourceName(evtType);
    return `Schedule a ${name}`;
  }
  // Show default title to customers
  return `Schedule an Appointment`;
};

const getTimeslotsForDate = async (
  evtType,
  schedulingData,
  timeslots,
  excludeTimeslotId,
  setLoading,
  setUnavailableTimeSlots,
  notify,
  isCurrent = () => true,
) => {
  if (!evtType?.id) {
    // Unsaved event type (create-form preview): no server availability to
    // check, but the caller already raised the backdrop — clear it.
    setLoading(false);
    return;
  }

  try {
    setLoading(true);

    // Same engine as save-time validateAppointment (dev's double-booking
    // fix): yard-wide bookings for the day ±1 with hydrated capacity caps,
    // compared on an absolute epoch-ms timeline — so the grid never renders
    // a slot that Confirm would then reject. The two reads are independent.
    const dateParts = getYardCalendarDateParts(schedulingData.date);
    const [allEventTypes, rows] = await Promise.all([
      fetchAllEventTypes(),
      fetchYardTimeslotsForDay(dateParts),
    ]);
    const eventTypeMap = new Map(allEventTypes.map((et) => [et.id, et]));
    // Exclude the booking being rescheduled so its own slot is not counted
    // against capacity (otherwise its current time shows as taken).
    const occupied = getOccupiedIntervals(rows, eventTypeMap, excludeTimeslotId);

    // The dropdown's active-only event types do not hydrate assetType, so
    // take the candidate's event type from the all-status fetch (by id).
    const candidateEventType = eventTypeMap.get(evtType.id) || evtType;
    const cap = getEventTypeCap(candidateEventType);
    const assetTypeId = candidateEventType.assetTypeId || null;

    const unavailableTimeSlots = {};
    (timeslots || []).forEach((slot) => {
      if (timeStringToMinutes(slot) === null) return;
      // Capacity math, not a write: an unconvertible slot (no yard time zone)
      // is left out of the blocked set rather than reported here — the write
      // path refuses the booking with a message of its own.
      const startDate = tryGetDateTimeFromTimeslot({
        ...dateParts,
        time: slot,
      });
      const start = startDate?.getTime();
      if (!Number.isFinite(start)) return;
      const candidate = {
        start,
        end:
          start +
          ((candidateEventType.duration ?? 0) +
            (candidateEventType.bufferTime ?? 0)) *
            60000,
        cap,
        assetTypeId,
      };
      if (isSlotBlocked(candidate, occupied)) {
        unavailableTimeSlots[slot] = true;
      }
    });

    if (!isCurrent()) return;
    setUnavailableTimeSlots(unavailableTimeSlots);
  } catch (err) {
    if (isCurrent()) {
      // Never leave the previous date's availability map applied to the new
      // date; save-time validation remains the authoritative gate.
      setUnavailableTimeSlots({});
      logError("Scheduler:getTimeslotsForDate", err, notify);
    }
  } finally {
    if (isCurrent()) setLoading(false);
  }
};

export const Scheduler = ({
  eventType,
  appType = window.appUser.appType,
  account,
  open,
  setIsOpen,
  resettable,
  selectedTimeslot,
  setSelectedTimeslot,
  schedulingOverride,
  setSchedulingOverride,
  workflow,
  inline = false,
}) => {
  const notify = useNotify();
  const classes = useStyles();
  const [evtType, setEvtType] = useState(eventType);
  const [eventTypes, setEventTypes] = useState([]);
  const [schedulingData, setSchedulingData] = useState({ date: new Date() });
  const [timeslots, setTimeslots] = useState([]);
  const [unavailableTimeSlots, setUnavailableTimeSlots] = useState({});
  const [validationError, setValidationError] = useState("");
  const [loading, setLoading] = useState(false);
  const [localSchedulingOverride, setLocalSchedulingOverride] = useState(false);
  const hasControlledSchedulingOverride =
    typeof setSchedulingOverride === "function";
  // Manager-only: the switch is hidden for everyone else, and the effective
  // value is gated too, so a caller that keeps an override flag in its own
  // state can never hand one to a supervisor's scheduler.
  const canOverride = canOverrideScheduling();
  const override =
    canOverride &&
    (hasControlledSchedulingOverride
      ? Boolean(schedulingOverride)
      : localSchedulingOverride);
  const updateOverride = hasControlledSchedulingOverride
    ? setSchedulingOverride
    : setLocalSchedulingOverride;
  const availabilityRequestRef = useRef(0);
  const submitRequestRef = useRef(0);
  const submittingRef = useRef(false);
  // Remember the persisted booking's id: an unsaved re-pick replaces
  // selectedTimeslot with an id-less temp slot, but capacity checks must
  // keep excluding the still-persisted original booking. Render-phase
  // setState (React's "adjust state during render" pattern) so reads never lag.
  const [persistedTimeslotId, setPersistedTimeslotId] = useState(
    selectedTimeslot?.id,
  );
  if (selectedTimeslot?.id && selectedTimeslot.id !== persistedTimeslotId) {
    setPersistedTimeslotId(selectedTimeslot.id);
  }
  const excludeTimeslotId = selectedTimeslot?.id || persistedTimeslotId;

  useEffect(
    () => () => {
      availabilityRequestRef.current += 1;
      submitRequestRef.current += 1;
    },
    [],
  );

  // Inline callers render the scheduler as part of a form and have no dialog to
  // dismiss, so they pass no setIsOpen.
  const closeScheduler = () => {
    if (setIsOpen) setIsOpen(false);
  };

  useEffect(() => {
    // YARD users always pick from the yard's event types. Other app types
    // (customer/carrier) fall back to the same list only when no specific
    // eventType was provided (no account-level default). Either way the list is
    // scoped to the caller's workflow (inbound vs outbound) when provided.
    if (appType === AppType.YARD || !eventType) {
      fetchEventTypes(setEventTypes, notify, workflow);
    }
  }, [appType, workflow, eventType]);

  const handleEventTypeChange = useCallback(
    async (value) => {
      setLoading(true);
      try {
        if (!value) return;
        const selectedEventType = eventTypes.find((et) => et.id === value);
        if (!selectedEventType) return;

        const { initializedEventType, schedulingData } = init(
          selectedTimeslot,
          selectedEventType,
          override
        );
        setSchedulingData(schedulingData);
        setEvtType(initializedEventType);
      } finally {
        setLoading(false);
      }
    },
    [eventTypes, selectedTimeslot, override],
  );

  useEffect(() => {
    if (!eventTypes?.length) return;

    // Internal dropdown selection wins over the eventType prop; the prop is
    // only a fallback so the in-dialog choice doesn't snap back (prop changes
    // are handled by the eventType-init effect below).
    const selectedId =
      evtType?.id || eventType?.id || selectedTimeslot?.eventTypeId;
    const fullEventType = eventTypes.find((et) => et.id === selectedId);

    if (fullEventType && fullEventType.id !== evtType?.id) {
      const initialized = init(selectedTimeslot, fullEventType, override);
      setSchedulingData(initialized.schedulingData);
      setEvtType(initialized.initializedEventType);
      return;
    }

    if (!selectedId && eventTypes.length === 1) {
      handleEventTypeChange(eventTypes[0]?.id);
    }
  }, [
    eventTypes,
    eventType?.id,
    evtType?.id,
    selectedTimeslot?.eventTypeId,
    override,
    handleEventTypeChange,
  ]);

  useEffect(() => {
    if (eventType) {
      // Initialize state when an eventType is provided
      const { initializedEventType, schedulingData } = init(
        selectedTimeslot,
        eventType,
        override
      );

      setEvtType(initializedEventType);
      setSchedulingData(schedulingData);
    }
  }, [eventType]);

  useEffect(() => {
    if (evtType) {
      // Initialize state when override changes.
      const { initializedEventType, schedulingData } = init(
        selectedTimeslot,
        evtType,
        override
      );

      setEvtType(initializedEventType);
      setSchedulingData(schedulingData);
    }
  }, [override]);

  useEffect(() => {
    if (!evtType?.holidays || !evtType?.minScheduleDay) return;
    // Land on the appointment being edited, not the first available day —
    // resolveSchedulerOpenDay falls back to minScheduleDay when the booked day
    // is no longer bookable. Deciding the day here rather than leaning on
    // handleDateChange's walk-forward, which is skipped past maxScheduleDay and
    // steps one day at a time unguarded.
    handleDateChange(
      resolveSchedulerOpenDay(evtType, selectedTimeslot, override),
    );
  }, [evtType]);

  const handleSelection = async (
    property,
    value,
    availableTimeslots = timeslots,
  ) => {
    const nextSchedulingData = { ...schedulingData, [property]: value };

    if (property !== "date") {
      setSchedulingData(nextSchedulingData);
      return;
    }

    nextSchedulingData.slot = "";

    // If the selectedTimeslot exists and matches the current date, highlight it
    if (
      selectedTimeslot &&
      isSameYardScheduleDay(
        value,
        new Date(
          selectedTimeslot.year,
          selectedTimeslot.month,
          selectedTimeslot.day
        )
      ) &&
      selectedTimeslot.eventTypeId === evtType.id
    ) {
      nextSchedulingData.slot = selectedTimeslot.time; // Pre-select the slot
    }

    const requestId = availabilityRequestRef.current + 1;
    availabilityRequestRef.current = requestId;
    await getTimeslotsForDate(
      evtType,
      nextSchedulingData,
      availableTimeslots,
      excludeTimeslotId,
      setLoading,
      setUnavailableTimeSlots,
      notify,
      () => requestId === availabilityRequestRef.current,
    ); // Load timeslots for the selected date

    if (requestId !== availabilityRequestRef.current) return;

    setSchedulingData(nextSchedulingData);
  };

  const isValid = () => {
    return schedulingData.date && schedulingData.slot ? true : false;
  };

  /**
   * Re-check the picked slot's availability and hand it to the caller.
   *
   * The slot arrives as an argument rather than being read off schedulingData:
   * inline mode commits from the click that selects it, which is the same
   * render as the setState that records it.
   */
  const commitTimeslot = async (slot, { close }) => {
    if (submittingRef.current) return;
    if (!account) {
      if (!setSelectedTimeslot) {
        // Preview window - ignore submit request.
        closeScheduler();
      }
      return;
    }
    submittingRef.current = true;
    const requestId = submitRequestRef.current + 1;
    submitRequestRef.current = requestId;
    try {
      setLoading(true);
      setValidationError("");
      const dateParts = getYardCalendarDateParts(schedulingData.date);
      const tempTimeslot = {
        groups: account.groups,
        status: EventType.RESERVED,
        reloadYardId: account.reloadYardId,
        accountId: account?.id,
        accountName: account?.businessName,
        eventTypeId: evtType.id,
        eventType: evtType,
        ...dateParts,
        time: slot,
        // Travels with the booking: validateAppointment below, the save
        // buttons' own re-check, and the lambda's server-side capacity sweep
        // all read it, so an overridden slot is accepted end to end. Only set
        // when on — a stored `false` would be written to the Timeslot row.
        ...(override ? { schedulingOverride: true } : {}),
      };
      // Exclude the booking being rescheduled so it isn't counted against
      // its own capacity.
      const error = await validateAppointment(tempTimeslot, excludeTimeslotId);
      if (requestId !== submitRequestRef.current) return;
      if (error) {
        setValidationError(`${tempTimeslot.time} is no longer available.`);
        // Inline mode highlighted the slot the instant it was clicked, before
        // this re-check ran (handleTimeSlotChange). A refusal means the slot was
        // never handed up, so clear the grid pick too — otherwise the
        // highlighted cell and the caller's empty value disagree, the very
        // mismatch handleTimeSlotChange guards against on a deselect. Dialog
        // mode keeps the pick so the Confirm user sees which slot was refused
        // and can choose another.
        if (inline) {
          setSchedulingData((prev) =>
            prev.slot === slot ? { ...prev, slot: "" } : prev,
          );
          setSelectedTimeslot(null);
        }
        return;
      }
      setSelectedTimeslot(tempTimeslot);
      if (close) closeScheduler();
    } catch (err) {
      if (requestId === submitRequestRef.current) {
        logError("Scheduler:commitTimeslot", err, notify);
      }
    } finally {
      const cleanup = finalizeAppointmentSubmit(
        requestId,
        submitRequestRef.current,
      );
      if (cleanup.shouldClearSubmitting) {
        submittingRef.current = false;
      }
      if (cleanup.shouldClearLoading) setLoading(false);
    }
  };

  const handleCreateAppointment = () =>
    commitTimeslot(schedulingData.slot, { close: true });

  const handleClearAppointment = () => {
    const dismissState = getDismissedSchedulerState(
      availabilityRequestRef.current,
      submitRequestRef.current,
    );
    availabilityRequestRef.current = dismissState.nextAvailabilityRequestId;
    submitRequestRef.current = dismissState.nextSubmitRequestId;
    if (dismissState.shouldClearSubmitting) {
      submittingRef.current = false;
    }
    if (dismissState.shouldClearLoading) {
      setLoading(false);
    }
    setUnavailableTimeSlots(dismissState.unavailableTimeSlots);
    if (dismissState.shouldClearValidationError) {
      setValidationError("");
    }
    setSelectedTimeslot(null);
    closeScheduler();
  };

  const handleDateChange = async (newValue) => {
    if (evtType) {
      setLoading(true);
      if (
        shouldDisableDate(evtType, newValue, override) &&
        newValue < evtType.maxScheduleDay
      ) {
        while (shouldDisableDate(evtType, newValue, override)) {
          newValue = add(newValue, { days: 1 });
        }
      }
      const dayTimeslots = generateDayTimeslots(
        newValue,
        evtType,
        override,
        setValidationError,
      );
      setTimeslots(dayTimeslots);
      await handleSelection("date", newValue, dayTimeslots);
    }
  };

  const handleMonthChange = async (newValue) => {
    handleDateChange(newValue);
  };

  const handleTimeSlotChange = (newValue) => {
    newValue = timeslots && schedulingData.slot === newValue ? "" : newValue;
    handleSelection("slot", newValue);
    // Inline mode has no Confirm button, so the click is the commit: it runs the
    // same availability re-check Confirm ran and then hands the slot up, and a
    // click that deselects clears the caller's pick so the form value and the
    // grid cannot disagree.
    if (!inline) return;
    if (!newValue) {
      setSelectedTimeslot(null);
      return;
    }
    commitTimeslot(newValue, { close: false });
  };

  const getSelectedDateText = () => {
    if (schedulingData.date) {
      return (
        formatYardScheduleDate(schedulingData.date, "EEEE, MMMM d") +
        (schedulingData.slot ? ` ${schedulingData.slot}` : "")
      );
    }
    return "Select a date";
  };

  const isDateDisabled = (day) => {
    return shouldDisableDate(evtType, day, override);
  };

  const renderDayInPicker = (
    date,
    selectedDate,
    dayInCurrentMonth,
    dayComponent
  ) => {
    // if (shouldDisableDate(date) || !dayInCurrentMonth) return dayComponent;
    if (isSameYardScheduleDay(date, selectedDate))
      return (
        <Box display="flex">
          <Avatar className={classes.selectedDateAvatar}>
            {getYardCalendarDateParts(selectedDate).day}
          </Avatar>
        </Box>
      );

    if (isSameYardScheduleDay(date, getYardNow())) {
      return (
        <Avatar className={classes.dateAvatar}>
          {getYardCalendarDateParts(date).day}
        </Avatar>
      );
    }
    return dayComponent;
  };

  const isTimeslotAtCapacity = (itm) => unavailableTimeSlots[itm] !== undefined;

  // The override marks a full slot rather than blocking it: the manager is
  // deliberately overbooking, so they need to see which slots they are
  // overbooking and still be able to pick one.
  const isTimeslotDisabled = (itm) =>
    !schedulingData.date || (isTimeslotAtCapacity(itm) && !override);

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
      return; // ignore backdrop clicks
    }
    const dismissState = getDismissedSchedulerState(
      availabilityRequestRef.current,
      submitRequestRef.current,
    );
    availabilityRequestRef.current = dismissState.nextAvailabilityRequestId;
    submitRequestRef.current = dismissState.nextSubmitRequestId;
    if (dismissState.shouldClearSubmitting) {
      submittingRef.current = false;
    }
    if (dismissState.shouldClearLoading) {
      setLoading(false);
    }
    setUnavailableTimeSlots(dismissState.unavailableTimeSlots);
    if (dismissState.shouldClearValidationError) {
      setValidationError("");
    }
    closeScheduler();
  };

  // The three panels the scheduler is made of, built once and then laid out
  // either as a dialog or, when `inline`, as a block inside the caller's form.
  const eventTypePanel =
    eventTypes.length > 1 ? (
      // More than one option (after workflow filtering): let the user
      // choose. Customers/carriers also see the selected event type's
      // description below the picker.
      <>
        <MuiTextfield
          select
          label="Event Type"
          variant="outlined"
          margin="dense"
          fullWidth
          required
          value={evtType?.id || ""}
          onChange={(event) => handleEventTypeChange(event.target.value)}
          SelectProps={{
            displayEmpty: true,
          }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="" disabled>
            Select an Event Type
          </MenuItem>
          {eventTypes.map((eventTypeOption) => (
            <MenuItem key={eventTypeOption.id} value={eventTypeOption.id}>
              {eventTypeOption.name}
            </MenuItem>
          ))}
        </MuiTextfield>
        {appType !== AppType.YARD && evtType?.description && (
          <Box mt={1} mb={2}>
            <Typography variant="body1" color="textPrimary">
              {evtType.description}
            </Typography>
            <Box mt={2}>
              <Divider />
            </Box>
          </Box>
        )}
      </>
    ) : appType === AppType.YARD ? (
      <Box mt={1} mb={1}>
        <Typography variant="caption" color="textSecondary">
          Event Type
        </Typography>
        <Typography variant="body1">
          {evtType?.name || eventTypes[0]?.name || "Select an Event Type"}
        </Typography>
      </Box>
    ) : (
      <>
        <Typography variant="body1" color="textPrimary">
          {evtType?.description}
        </Typography>
        <Box mb={2} mt={2}>
          <Divider />
        </Box>
      </>
    );

  const calendarPanel = (
    <>
      <Box
        // this box horizontally centers the inner calendar box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          // this box constrain the calendar to width=300
          display="flex"
          flexDirection="column"
          alignItems="strech"
        >
          <Backdrop
            open={loading}
            className={inline ? classes.inlineBackdrop : classes.backdrop}
          >
            <CircularProgress />
          </Backdrop>
          <Calendar
            theme={{
              todayTextColor: "blue",
              selectedDayBackgroundColor: "red",
            }}
            date={schedulingData.date}
            onChange={handleDateChange}
            onMonthChange={handleMonthChange}
            shouldDisableDate={isDateDisabled}
            minDate={
              // Override allows backfilling past dates (dev parity).
              override ? undefined : startOfDay(getYardNow())
            }
            maxDate={evtType ? evtType.maxScheduleDay : undefined} // Only set if evtType is present
            allowKeyboardControl={false}
            renderDay={renderDayInPicker}
            disabled={!evtType}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="strech"
        flex={2}
      >
        <Box mt={2} mb={1}>
          <Typography gutterBottom variant="subtitle2">
            {getSelectedDateText()}
          </Typography>
          <Box mb={0.5} display="flex" alignItems="center">
            <ScheduleIcon fontSize="sm"></ScheduleIcon>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              component="span"
            >
              &nbsp; {evtType?.duration} mins
            </Typography>
          </Box>
          <Box mb={0.5} display="flex" alignItems="center">
            <PublicIcon fontSize="sm"></PublicIcon>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              component="span"
            >
              &nbsp; {getYardTimeZoneName()}
            </Typography>
          </Box>
          {appType === AppType.YARD && canOverride && (
            <Box mb={0.5} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={override}
                    onChange={() => updateOverride(!override)}
                    disabled={!evtType}
                  />
                }
                label="Override Validation"
              />
            </Box>
          )}
          {validationError && (
            <Typography variant="caption" color="error">
              {validationError}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );

  const timeslotPanel = (
    <Box className={inline ? classes.inlineTimeslots : classes.timeslots}>
      {timeslots.map((itm) => {
        const atCapacity = isTimeslotAtCapacity(itm);
        const disabled = isTimeslotDisabled(itm);
        const selected = schedulingData.slot === itm;
        return (
          <Tooltip key={itm} title={atCapacity ? "At capacity" : ""}>
            <span>
              <MuiButton
                color="primary"
                fullWidth
                variant={selected ? "contained" : "outlined"}
                onClick={() => handleTimeSlotChange(itm)}
                disabled={disabled}
                className={[
                  classes.timeslot,
                  // Only under the override is a full slot rendered enabled,
                  // and then it must not look like an ordinary free one.
                  atCapacity && !disabled
                    ? selected
                      ? classes.timeslotAtCapacitySelected
                      : classes.timeslotAtCapacity
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {itm}
              </MuiButton>
            </span>
          </Tooltip>
        );
      })}
    </Box>
  );

  // Inline: no title, no Confirm — the caller's own form is the frame and the
  // slot click already committed the pick. The calendar keeps its intrinsic
  // width and the timeslots wrap below it when the column is too narrow to
  // hold both, so the layout answers to the column rather than the viewport.
  if (inline)
    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <Box className={classes.inlineRoot}>
          {eventTypePanel}
          <Box display="flex" flexWrap="wrap" alignItems="flex-start">
            <Box className={classes.inlineCalendar}>{calendarPanel}</Box>
            {timeslotPanel}
          </Box>
          {resettable && (
            <Box mt={1}>
              <Button
                label="CLEAR"
                variant="text"
                color="default"
                onClick={handleClearAppointment}
              >
                <ClearIcon />
              </Button>
            </Box>
          )}
        </Box>
      </MuiPickersUtilsProvider>
    );

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Dialog open={open} onClose={handleClose} disableEscapeKeyDown={false}>
        <DialogTitle component="span">{getTitle(evtType, appType)}</DialogTitle>
        <IconButton
          onClick={handleClose}
          edge="start"
          className={classes.dialogCloseIcon}
        >
          <HighlightOffIcon fontSize="large" />
        </IconButton>
        <DialogContent dividers className={classes.dialogContent}>
          <Grid container spacing={0} style={{ height: "100%" }}>
            <Grid item xs={12}>
              {eventTypePanel}
            </Grid>

            {/* Left side: fixed content */}
            <Grid item xs={9}>
              {calendarPanel}
            </Grid>
            {/* Right side: timeslots */}
            <Grid
              item
              xs={3}
              style={{
                display: "flex",
                flexDirection: "column",
                height: "90%",
              }}
            >
              {/* This Box will fill the remaining space and scroll vertically */}
              {timeslotPanel}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          {resettable && (
            <Button
              label="CLEAR"
              variant="text"
              color="default"
              onClick={handleClearAppointment}
            >
              <ClearIcon />
            </Button>
          )}
          <div className={classes.spacer}></div>
          <Button
            label="Cancel"
            variant="text"
            color="default"
            onClick={handleClose}
          >
            <CancelIcon />
          </Button>
          <Button
            label="Confirm"
            variant="text"
            color="primary"
            disabled={!isValid() || loading}
            onClick={handleCreateAppointment}
          >
            <CheckIcon />
          </Button>
        </DialogActions>
      </Dialog>
    </MuiPickersUtilsProvider>
  );
};
